import React, { useCallback } from "react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { UserProfile } from "../types/user";
import { authStorage } from "../utils/storage";

interface DecodedToken {
  nameid: string;
  email: string;
  exp: number;
}

interface AuthContextType {
  isAuthenticated: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
  user: DecodedToken | null;
  isCompany: boolean;
  profile: UserProfile | null;
  refreshProfile: () => void;
  userId?: string;
  setToken: (token: string | null) => void;
  token: string | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<DecodedToken | null>(null);
  const [loading, setLoading] = useState(true);

  const [token, setToken] = useState<string | null>(() =>
    authStorage.getAccessToken(),
  );

  const [profile, setProfile] = useState<UserProfile | null>(null);

  const userId = profile?.id;
  const isCompany = profile?.type === "company";

  const navigate = useNavigate();

  const logout = useCallback(() => {
    authStorage.removeAccessToken();
    setToken(null);
    navigate("/signin");
    setUser(null);
    setAuthenticated(false);
  }, [navigate]);

  useEffect(() => {
    const storedToken = authStorage.getAccessToken();

    const init = async () => {
      if (storedToken) {
        try {
          const decoded = jwtDecode<DecodedToken>(storedToken);
          const currentTime = Date.now() / 1000;

          if (decoded.exp < currentTime) {
            logout();
          } else {
            setToken(storedToken);
            setAuthenticated(true);
            setUser(decoded);
            await refreshProfile();
          }
        } catch (e) {
          logout();
        }
      }

      setLoading(false);
    };

    init();
  }, [logout]);

  const login = async (token: string): Promise<void> => {
    setToken(token);
    authStorage.setAccessToken(token);

    const decoded = jwtDecode<DecodedToken>(token);
    setUser(decoded);
    setAuthenticated(true);
    await refreshProfile();
  };

  const refreshProfile = async () => {
    const token = authStorage.getAccessToken();
    if (!token) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();

        setProfile(data);
      } else {
        console.error("Failed to load profile", res.status);
      }
    } catch (e) {
      console.error("Error fetching profile", e);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        user,
        isCompany,
        profile,
        refreshProfile,
        userId,
        setToken,
        token,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
